import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Restore as RestoreIcon,
  Refresh as RefreshIcon,
  Visibility as CoverageIcon,
  Warning as WarningIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { ensureUiScope } from '../services/api';
import { blueprintsApi, BlueprintMeta, type BlueprintCompareResponse } from '../services/blueprintsApi';
import { useAuth } from '../contexts/AuthContext';

function formatBytes(n?: number | null): string {
  if (!n || n <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function ConfigSnapshots() {
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteBlueprints = capabilities.includes('blueprints:write');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<BlueprintMeta[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<BlueprintMeta | null>(null);
  const [restoreDryRun, setRestoreDryRun] = useState<any | null>(null);
  const [restoring, setRestoring] = useState(false);

  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageTarget, setCoverageTarget] = useState<BlueprintMeta | null>(null);

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareFrom, setCompareFrom] = useState<string>('');
  const [compareTo, setCompareTo] = useState<string>('');
  const [comparing, setComparing] = useState(false);
  const [compareResult, setCompareResult] = useState<BlueprintCompareResponse | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const scope = useMemo(() => ensureUiScope(), []);
  const orgId = String(scope.orgId || '').trim();
  const siteId = String(scope.siteId || '').trim();

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!orgId || !siteId) {
        setItems([]);
        setError('Org/site scope is not set yet. Re-login or choose scope from the header.');
        return;
      }
      const res = await blueprintsApi.list(orgId, siteId);
      setItems(res.blueprints || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load snapshots');
    } finally {
      setLoading(false);
    }
  }, [orgId, siteId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onCreate = useCallback(async () => {
    const nm = createName.trim();
    if (!nm) return;
    setCreating(true);
    try {
      if (!orgId || !siteId) {
        throw new Error('Org/site scope is not set yet. Re-login or choose scope from the header.');
      }
      const res = await blueprintsApi.create({ name: nm, description: createDescription.trim() || undefined, org_id: orgId, site_id: siteId });
      setSnackbar({ open: true, severity: 'success', message: `Snapshot created: ${res.name}` });
      setCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      await fetchList();
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Failed to create snapshot' });
    } finally {
      setCreating(false);
    }
  }, [createName, createDescription, fetchList, orgId, siteId]);

  const onDownload = useCallback(async (bp: BlueprintMeta) => {
    try {
      const blob = await blueprintsApi.download(bp.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `config_snapshot_${bp.name}_${bp.id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Download failed' });
    }
  }, []);

  const openRestore = useCallback(async (bp: BlueprintMeta) => {
    setRestoreTarget(bp);
    setRestoreOpen(true);
    setRestoreDryRun(null);
    setRestoring(true);
    try {
      const res = await blueprintsApi.restore(bp.id, { dry_run: true, restore_mode: 'merge' });
      setRestoreDryRun(res.summary || null);
    } catch (e: any) {
      setRestoreDryRun({ error: e?.message || 'Dry-run failed' });
    } finally {
      setRestoring(false);
    }
  }, []);

  const openCoverage = useCallback((bp: BlueprintMeta) => {
    setCoverageTarget(bp);
    setCoverageOpen(true);
  }, []);

  const openCompare = useCallback(() => {
    setCompareResult(null);
    setCompareOpen(true);
    // Best-effort defaults: compare newest (index 0) vs previous (index 1)
    const sorted = [...(items || [])].sort((a, b) => {
      const ra = typeof a.revision === 'number' ? a.revision : -1;
      const rb = typeof b.revision === 'number' ? b.revision : -1;
      if (rb !== ra) return rb - ra;
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
    if (sorted.length >= 2) {
      setCompareTo(sorted[0].id);
      setCompareFrom(sorted[1].id);
    } else if (sorted.length === 1) {
      setCompareTo(sorted[0].id);
      setCompareFrom('');
    }
  }, [items]);

  const runCompare = useCallback(async () => {
    if (!compareFrom || !compareTo || compareFrom === compareTo) return;
    setComparing(true);
    setCompareResult(null);
    try {
      const res = await blueprintsApi.compare(compareFrom, compareTo);
      setCompareResult(res);
    } catch (e: any) {
      setCompareResult(null);
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Compare failed' });
    } finally {
      setComparing(false);
    }
  }, [compareFrom, compareTo]);

  const applyRestore = useCallback(async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await blueprintsApi.restore(restoreTarget.id, { apply: true, restore_mode: 'merge' });
      setSnackbar({ open: true, severity: 'success', message: `Restore applied (restore_id=${res.restore_id})` });
      setRestoreOpen(false);
      setRestoreTarget(null);
      setRestoreDryRun(null);
      await fetchList();
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Restore failed' });
    } finally {
      setRestoring(false);
    }
  }, [restoreTarget, fetchList]);

  const restoreDiff = useMemo(() => restoreDryRun?.diff || null, [restoreDryRun]);
  const coverageManifest = useMemo(() => (coverageTarget as any)?.payload_manifest || null, [coverageTarget]);
  const coverageSummary = useMemo(() => (coverageTarget as any)?.payload_summary || null, [coverageTarget]);
  const coverageByDevice = useMemo(() => {
    const by = coverageManifest?.by_device;
    return Array.isArray(by) ? by : [];
  }, [coverageManifest]);

  const compareOptions = useMemo(() => {
    const sorted = [...(items || [])].sort((a, b) => {
      const ra = typeof a.revision === 'number' ? a.revision : -1;
      const rb = typeof b.revision === 'number' ? b.revision : -1;
      if (rb !== ra) return rb - ra;
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
    return sorted;
  }, [items]);

  const compareLabel = useCallback((bp: BlueprintMeta) => {
    const rev = typeof bp.revision === 'number' ? `r${bp.revision}` : 'legacy';
    const ts = bp.created_at ? new Date(bp.created_at).toLocaleString() : '';
    return `${rev} · ${bp.name}${ts ? ` · ${ts}` : ''}`;
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Control-plane Snapshots</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchList} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<CompareIcon />} onClick={openCompare} disabled={loading || items.length < 2}>
            Compare…
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={!canWriteBlueprints}>
            Create Snapshot
          </Button>
        </Box>
      </Box>

      {!canWriteBlueprints ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to control-plane snapshots. Create and restore actions are disabled.
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Alert severity="info">
          These snapshots capture <b>control-plane (org/site)</b> config-only state (devices, cameras, zones, lines, policies, routing). They do not
          include incidents, evidence, users, or RBAC memberships. Restores are <b>transactional</b> and start with a dry-run diff.
        </Alert>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                <Skeleton variant="text" width={120} />
                <Skeleton variant="rounded" width={40} height={20} />
                <Skeleton variant="text" width={60} />
              </Box>
            ))}
          </Box>
        ) : (
          <List dense>
            {items.length === 0 ? (
              <ListItem>
                <ListItemText primary="No snapshots yet." secondary="Create one before major changes or wipes." />
              </ListItem>
            ) : (
              items.map((bp) => (
                <ListItem key={bp.id} sx={{ alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 700 }}>{bp.name}</Typography>
                        {typeof bp.revision === 'number' ? <Chip size="small" color="primary" label={`r${bp.revision}`} /> : null}
                        <Chip size="small" label={formatBytes(bp.payload_size_bytes)} />
                        {typeof (bp as any)?.payload_manifest?.warning_count === 'number' && (bp as any).payload_manifest.warning_count > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            icon={<WarningIcon />}
                            label={`${(bp as any).payload_manifest.warning_count} warning${(bp as any).payload_manifest.warning_count === 1 ? '' : 's'}`}
                          />
                        ) : null}
                        {bp.created_at && <Chip size="small" variant="outlined" label={new Date(bp.created_at).toLocaleString()} />}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {bp.description ? <Typography color="text.secondary">{bp.description}</Typography> : null}
                        {(bp as any)?.payload_summary && typeof (bp as any).payload_summary === 'object' ? (
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                            {Object.entries((bp as any).payload_summary as Record<string, any>)
                              .slice(0, 8)
                              .map(([k, v]) => (
                                <Chip key={k} size="small" variant="outlined" label={`${k}: ${String(v)}`} />
                              ))}
                          </Box>
                        ) : null}
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                          id={bp.id} {bp.payload_sha256 ? `· sha=${bp.payload_sha256}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Button size="small" variant="outlined" startIcon={<CoverageIcon />} onClick={() => openCoverage(bp)}>
                      Coverage
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => onDownload(bp)}>
                      Download
                    </Button>
                    <Button size="small" variant="contained" startIcon={<RestoreIcon />} onClick={() => openRestore(bp)} disabled={!canWriteBlueprints}>
                      Restore
                    </Button>
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        )}
      </Paper>

      {/* Coverage dialog */}
      <Dialog open={coverageOpen} onClose={() => setCoverageOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Snapshot coverage</DialogTitle>
        <DialogContent>
          {!coverageTarget ? (
            <Alert severity="info">No snapshot selected.</Alert>
          ) : (
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{coverageTarget.name}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                {typeof coverageTarget.revision === 'number' ? `r${coverageTarget.revision} · ` : ''}
                id={coverageTarget.id}
              </Typography>

              {coverageSummary ? (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                  {Object.entries(coverageSummary as Record<string, any>).map(([k, v]) => (
                    <Chip key={k} size="small" variant="outlined" label={`${k}: ${String(v)}`} />
                  ))}
                </Box>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This looks like a legacy snapshot (no summary/manifest stored). Create a new snapshot to get coverage proof.
                </Alert>
              )}

              {coverageManifest?.warning_count > 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Integrity warnings: <b>{String(coverageManifest.warning_count)}</b>
                </Alert>
              ) : null}

              <Divider sx={{ my: 2 }} />

              {coverageByDevice.length === 0 ? (
                <Alert severity="info">No per-device manifest available for this snapshot.</Alert>
              ) : (
                <List dense>
                  {coverageByDevice.map((d: any) => {
                    const camsById = d?.cameras_by_id && typeof d.cameras_by_id === 'object' ? d.cameras_by_id : {};
                    const camIds = Object.keys(camsById);
                    return (
                      <ListItem key={String(d?.device_id || 'unknown')} sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 700 }}>{String(d?.device_id || 'unknown')}</Typography>
                              <Chip size="small" label={`cameras: ${String(d?.cameras ?? 0)}`} />
                              <Chip size="small" label={`zones: ${String(d?.zones ?? 0)}`} />
                              <Chip size="small" label={`lines: ${String(d?.lines ?? 0)}`} />
                              <Chip size="small" label={`policies: ${String(d?.policies ?? 0)}`} />
                              <Chip size="small" label={`routing: ${String(d?.routing ?? 0)}`} />
                            </Box>
                          }
                          secondary={
                            camIds.length > 0 ? (
                              <Box sx={{ mt: 0.75, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {camIds.slice(0, 24).map((cid) => {
                                  const c = camsById[cid] || {};
                                  return (
                                    <Chip
                                      key={cid}
                                      size="small"
                                      variant="outlined"
                                      label={`${cid}: z${String(c.zones ?? 0)} l${String(c.lines ?? 0)} r${String(c.routing ?? 0)}`}
                                    />
                                  );
                                })}
                                {camIds.length > 24 ? <Chip size="small" variant="outlined" label={`+${camIds.length - 24} more`} /> : null}
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>No cameras captured for this device.</Typography>
                            )
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoverageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onClose={() => setCompareOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compare snapshots</DialogTitle>
        <DialogContent>
          {items.length < 2 ? (
            <Alert severity="info">Create at least two snapshots to compare.</Alert>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="From"
                  value={compareFrom}
                  onChange={(e) => setCompareFrom(e.target.value)}
                  sx={{ minWidth: 320 }}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select…</option>
                  {compareOptions.map((bp) => (
                    <option key={bp.id} value={bp.id}>
                      {compareLabel(bp)}
                    </option>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="To"
                  value={compareTo}
                  onChange={(e) => setCompareTo(e.target.value)}
                  sx={{ minWidth: 320 }}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select…</option>
                  {compareOptions.map((bp) => (
                    <option key={bp.id} value={bp.id}>
                      {compareLabel(bp)}
                    </option>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  startIcon={<CompareIcon />}
                  onClick={runCompare}
                  disabled={!compareFrom || !compareTo || compareFrom === compareTo || comparing}
                >
                  Compare
                </Button>
                {comparing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Comparing…</Typography>
                  </Box>
                ) : null}
              </Box>

              {compareResult ? (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    Comparing{' '}
                    <b>{compareResult.from_revision != null ? `r${compareResult.from_revision}` : 'legacy'}</b> →{' '}
                    <b>{compareResult.to_revision != null ? `r${compareResult.to_revision}` : 'legacy'}</b>
                  </Alert>

                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                    {Object.entries(compareResult.per_entity || {}).map(([k, c]: any) => (
                      <Chip key={k} size="small" variant="outlined" label={`${k}: +${c.added} -${c.removed} ~${c.modified}`} />
                    ))}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography sx={{ fontWeight: 700, mb: 1 }}>By device</Typography>
                  <List dense>
                    {Object.entries(compareResult.by_device_counts || {})
                      .sort((a: any, b: any) => {
                        const ta = (a[1]?.added || 0) + (a[1]?.removed || 0) + (a[1]?.modified || 0);
                        const tb = (b[1]?.added || 0) + (b[1]?.removed || 0) + (b[1]?.modified || 0);
                        return tb - ta;
                      })
                      .map(([deviceId, c]: any) => {
                        const total = (c?.added || 0) + (c?.removed || 0) + (c?.modified || 0);
                        if (total <= 0) return null;
                        return (
                          <ListItem key={deviceId}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontWeight: 700 }}>{deviceId}</Typography>
                                  <Chip size="small" label={`+${c.added || 0}`} />
                                  <Chip size="small" label={`-${c.removed || 0}`} />
                                  <Chip size="small" label={`~${c.modified || 0}`} />
                                </Box>
                              }
                              secondary={
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                  Total changes: {total}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                      })}
                  </List>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Snapshot</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={onCreate} variant="contained" disabled={!canWriteBlueprints || creating || !createName.trim()}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Restore Snapshot</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700 }}>{restoreTarget?.name}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>id={restoreTarget?.id}</Typography>
          <Divider sx={{ my: 2 }} />

          {restoring ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography>Computing dry-run diff…</Typography>
            </Box>
          ) : restoreDryRun?.error ? (
            <Alert severity="error">{restoreDryRun.error}</Alert>
          ) : (
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Dry-run summary</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {JSON.stringify(restoreDryRun, null, 2).slice(0, 12000)}
                </pre>
              </Paper>

              {restoreDiff ? (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Diff (truncated)</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {JSON.stringify(restoreDiff, null, 2).slice(0, 12000)}
                    </pre>
                  </Paper>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)} disabled={restoring}>
            Close
          </Button>
          <Button onClick={applyRestore} variant="contained" disabled={!canWriteBlueprints || restoring || !restoreTarget}>
            Apply Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
